const CONTENT_ATTR = 'data-ck-accordion-content'
const COLLAPSED_ATTR = 'data-ck-collapsed'
const INNER_CLASS = 'ck-accordion_inner'

/**
 * Initializes the accordion functionality.
 *
 * Motion lives in CSS (Webflow → Custom Code component → custom-styles embed): the content element becomes a one-row grid whose
 * row animates between 0fr and 1fr, so no height measuring is needed. While collapsed the
 * content is `inert`, `aria-hidden` and `visibility: hidden`, so nothing inside it can be
 * clicked, tabbed to, or read by assistive tech.
 *
 * @example
 * ```html
 * <div ck-accordion-group> <!-- optional: only one open at a time -->
 *   <div ck-accordion>
 *     <button>Accordion 1</button>
 *     <div>Content 1</div>
 *   </div>
 * </div>
 * ```
 */
let nextId = 0

/** `root` narrows the search: rows fetched later (see incidentsMore.ts) are initialised on their own. */
export default function initAccordion(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[ck-accordion]').forEach((accordion) => {
    if (accordion.hasAttribute('data-ck-accordion-ready')) return
    accordion.setAttribute('data-ck-accordion-ready', '')
    const index = nextId++
    const button = accordion.querySelector<HTMLElement>(':scope > button')
    const content = button?.nextElementSibling as HTMLElement | null
    if (!button || !content) return

    wrapContent(content)
    ensureAriaControlsAndID(button, content, index)
    setCollapsed(button, content, true)

    button.addEventListener('click', () => toggleAccordion(button, content))
  })
}

/** Moves the content's children into an inner wrapper so the grid-row animation can clip them. */
const wrapContent = (content: HTMLElement): void => {
  content.setAttribute(CONTENT_ATTR, '')
  if (content.querySelector(`:scope > .${INNER_CLASS}`)) return

  const inner = document.createElement('div')
  inner.className = INNER_CLASS
  while (content.firstChild) inner.appendChild(content.firstChild)
  content.appendChild(inner)
}

const toggleAccordion = (button: HTMLElement, content: HTMLElement): void => {
  const isExpanded = button.getAttribute('aria-expanded') === 'true'
  const group = button.closest<HTMLElement>('[ck-accordion-group]')

  if (group) {
    group.querySelectorAll<HTMLElement>('[ck-accordion]').forEach((other) => {
      const otherButton = other.querySelector<HTMLElement>(':scope > button')
      const otherContent = otherButton?.nextElementSibling as HTMLElement | null
      if (otherButton && otherContent && otherButton !== button) setCollapsed(otherButton, otherContent, true)
    })
  }

  setCollapsed(button, content, isExpanded)
}

const setCollapsed = (button: HTMLElement, content: HTMLElement, collapsed: boolean): void => {
  button.setAttribute('aria-expanded', String(!collapsed))
  content.setAttribute('aria-hidden', String(collapsed))
  content.toggleAttribute(COLLAPSED_ATTR, collapsed)
  // `inert` blocks focus, clicks and AT exposure for everything inside the closed drawer.
  content.toggleAttribute('inert', collapsed)
}

/**
 * Ensures the ARIA controls and ID requirements are set correctly.
 * If the content element doesn't have an ID, it assigns a unique one based on the index.
 */
const ensureAriaControlsAndID = (button: HTMLElement, content: HTMLElement, index: number): void => {
  content.id = content.id || `accordion-content-${index}`
  button.setAttribute('aria-controls', content.id)
}
